import { motion } from "framer-motion";
import { ArrowRight, BadgeCheck, MessagesSquare, HardHat, Clock } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { scrollToSection } from "@/lib/scroll";

const ABOUT_IMAGE =
  "https://images.unsplash.com/photo-1759922378222-47ad736a174d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHxjb25zdHJ1Y3Rpb24lMjBlbmdpbmVlciUyMHNhZmV0eSUyMHRlYW0lMjBzaXRlJTIwaW5zcGVjdGlvbnxlbnwwfHx8fDE3OTE4MTYzNzF8MA&ixlib=rb-4.1.0&q=85";

const COMMITMENTS = [
  {
    icon: BadgeCheck,
    title: "Kualitas Pekerjaan",
    text: "Setiap detail dikerjakan sesuai standar dan spesifikasi yang disepakati.",
  },
  {
    icon: MessagesSquare,
    title: "Komunikasi Jelas",
    text: "Perkembangan proyek disampaikan secara terbuka dan mudah dipahami.",
  },
  {
    icon: HardHat,
    title: "Keselamatan Kerja",
    text: "Prosedur keselamatan diterapkan di setiap tahap pengerjaan.",
  },
  {
    icon: Clock,
    title: "Ketepatan Waktu",
    text: "Jadwal direncanakan realistis dan dipantau hingga serah terima.",
  },
];

export const About = () => (
  <section id="tentang" className="bg-white" aria-label="Tentang kami">
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-12 lg:py-28">
      <div>
        <Reveal>
          <Eyebrow>Tentang Kami</Eyebrow>
          <h2
            data-testid="about-heading"
            className="text-balance font-heading text-3xl font-extrabold leading-tight tracking-tight text-navy sm:text-4xl"
          >
            Mitra Konstruksi untuk Setiap{" "}
            <span className="italic text-orange-500">Rencana Anda</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-base leading-relaxed text-slate-600">
            Kami percaya bahwa setiap bangunan berawal dari kepercayaan.
            Karena itu, kami berkomitmen menjaga kualitas pekerjaan, menjalin
            komunikasi yang jelas sejak awal, mengutamakan keselamatan kerja di
            lokasi proyek, dan menyelesaikan setiap pekerjaan tepat waktu.
          </p>
        </Reveal>
        <Reveal delay={0.18}>
          <ul
            data-testid="about-commitment-list"
            className="mt-9 grid gap-5 sm:grid-cols-2"
          >
            {COMMITMENTS.map((item) => (
              <li key={item.title} className="flex gap-3.5">
                <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-500/10">
                  <item.icon className="h-5 w-5 text-orange-600" aria-hidden="true" />
                </span>
                <span>
                  <span className="block font-heading text-sm font-bold text-navy">
                    {item.title}
                  </span>
                  <span className="mt-1 block text-sm leading-relaxed text-slate-500">
                    {item.text}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.26}>
          <a
            href="#layanan"
            data-testid="about-cta-button"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection("#layanan");
            }}
            className="group mt-9 inline-flex items-center gap-2.5 rounded-full bg-navy px-7 py-3.5 text-sm font-bold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-navy-light"
          >
            Kenali Kami
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </Reveal>
      </div>

      <Reveal delay={0.15} className="relative">
        <div
          className="absolute -right-4 -top-4 hidden h-full w-full rounded-2xl border-2 border-orange-500 sm:block"
          aria-hidden="true"
        />
        <motion.figure
          whileHover={{ scale: 0.985 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden rounded-2xl shadow-2xl shadow-navy/20"
        >
          <img
            data-testid="about-image-team"
            src={ABOUT_IMAGE}
            alt="Tim lapangan sedang meninjau pekerjaan di lokasi proyek konstruksi"
            className="aspect-[4/3] w-full object-cover"
            loading="lazy"
          />
        </motion.figure>
      </Reveal>
    </div>
  </section>
);
